import { runWithErrorReporting } from "$root/api/error-reporting";
import { platformToast } from "$root/notification-system/toast";
import { parallelize } from "$root/utils";
import type {
	CompositionTemplateEntityType,
	EditorSelectableBasketsRequestEntityEnum,
	ReviewTicker,
} from "@eu.mdotm.api/sphere";
import type { ActionWithOptionalGroup } from "@mdotm/shadcn/legacy/components";
import { type MaybeFn, noop } from "@mdotm/shadcn/legacy/utils";
import BigNumber from "bignumber.js";
import Immutable, { type Map, OrderedMap, OrderedSet, type Set } from "immutable";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	createInstrumentList,
	instrumentsRepository,
	reviewTickerToInstrumentsMap,
	tickersToInstrumentMap,
} from "../instruments/repository";
import { portfolioRepository } from "../portfolio/repository";
import type { Instrument } from "./const";
import type { QuantityEditorBuilderProps } from "./quantity/builder";
import { spawnCopyCompositionDialog } from "./spawn/copy-composition";
import { spawnInstrumentAddersDialog } from "./spawn/instrument-adder";
import { spawnInstrumentUploadDialogProps } from "./spawn/instrument-upload";
import { spawnPortfoliosAdderDialog } from "./spawn/portfolio-adder";

export function cashTickerToInstrument(cashTicker: ReviewTicker): Instrument {
	return {
		id: cashTicker.ticker!,
		instrumentType: "INSTRUMENT",
		tickerName: cashTicker.ticker,
		name: cashTicker.instrument ?? "Cash",
		alias: cashTicker.alias,
		weight: cashTicker.weight,
		proxyOverwriteType: cashTicker.proxyOverwriteType,
		riskModel: cashTicker.proxies,
	};
}

export function covertInstrumentEditorToReviewTicker(composition: Instrument[]): ReviewTicker[] {
	return composition.map((instrument): ReviewTicker => {
		return {
			proxies: instrument.riskModel?.map((x) => ({ ticker: x.ticker, weight: x.weight })),
			alias: instrument.alias,
			isin: instrument.alias,
			ticker: instrument.tickerName,
			weight: instrument.weight,
			proxyOverwriteType: instrument.proxyOverwriteType,
		};
	});
}

/**
 * Normalise composition and {@link verifySomeInstrumentsAreExcluded} if portfolio are included in composition
 * @param params
 */
export async function normaliseAndVerifyInstrumentPortfolioComposition(params: {
	selection: Immutable.Set<string>;
	entity: EditorSelectableBasketsRequestEntityEnum;
	instrumentBuilder: InstrumentEditorBuilderProps;
}): Promise<void> {
	const { entity, selection, instrumentBuilder } = params;
	instrumentBuilder.normalise(selection);
	const latestComposition = Array.from(instrumentBuilder.latestComposition().values());
	const portfolios = latestComposition.filter((x) => x.proxyOverwriteType === "PORTFOLIO_MIXED" && selection.has(x.id));
	if (portfolios.length > 0) {
		await parallelize(
			portfolios.map((portfolio) => async () => {
				const removedTickers = await portfolioRepository.verifyInvestmentNormalizedComposition.fetchQuery({
					entity,
					investmentUuid: portfolio.investmentUuid!,
					weight: portfolio.weight ?? 0,
				});
				instrumentBuilder.setInstrument(portfolio.id, {
					...portfolio,
					someInstrumentsAreExcluded: removedTickers?.length > 0,
					nOfInstrumentExcluded: removedTickers?.length,
				});
			}),
			{ concurrency: 3 },
		);
	}
}

type HeaderActions = "addInstruments" | "addPortfolio" | "uploadInstruments" | "copyComposition";
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useCompositionHeaderAction({
	instrumentBuilder,
	quantityBuilder,
	entity,
	uuid,
}: {
	instrumentBuilder: InstrumentEditorBuilderProps;
	/** The same builder, when the composition is edited in quantities: an upload then goes to it by quantity. */
	quantityBuilder?: QuantityEditorBuilderProps;
	entity: EditorSelectableBasketsRequestEntityEnum;
	uuid: string | undefined;
}) {
	return useMemo(
		() =>
			({
				addInstruments: () => ({
					label: "Add instruments",
					onClick: () => {
						const compositionMap = instrumentBuilder.getComposition();
						const compositionWithoutDeletedMap = instrumentBuilder.getComposition({ removeDeleted: true });

						spawnInstrumentAddersDialog({
							entity,
							uuid,
							instrumentsAlreadySelected: Array.from(compositionWithoutDeletedMap.values()),
							async onSubmitAsync(tickerIds) {
								const userInstrumentsMap = await tickersToInstrumentMap(tickerIds.filter((x) => x != null));
								const instrumentsWithRowId = tickerIds.map((ticker): Instrument => {
									const userInstrument = userInstrumentsMap.get(ticker) ?? {};
									const id = userInstrument.tickerName!;

									return {
										...userInstrument,
										id,
										instrumentType: "INSTRUMENT",
										weight: entity === "UNIVERSE" ? undefined : 0,
										deleted: false,
										alias: userInstrument.identifiers?.ALIAS,
									};
								});
								instrumentBuilder.bulkAddInstruments(instrumentsWithRowId);
							},
							onSubmitMissingInstrument(instrument) {
								const id = instrument.identifier;
								if (!compositionMap.has(id)) {
									instrumentBuilder.addInstrument(id, {
										id,
										alias: instrument.identifier,
										weight: entity === "UNIVERSE" ? undefined : instrument.weight,
										instrumentType: "INSTRUMENT",
										deleted: false,
										name: `Instrument ${instrument.identifier}`,
									});
								}
							},
						});
					},
					"data-qualifier": "CompositionEditor/HeaderAction/DropdownMenu(AddInstrument)",
				}),
				addPortfolio: () => ({
					label: "Add portfolio",
					onClick: () =>
						spawnPortfoliosAdderDialog({
							uuid,
							entity,
							instrumentsInComposition: Array.from(instrumentBuilder.getComposition().values()),
							async onSubmitAsync(investments) {
								const investmentData = await instrumentsRepository.investmentsRichInfo.fetchQuery(
									investments.map((x) => ({
										ticker: x.uuid,
										proxyOverwriteType: "PORTFOLIO_MIXED",
										weight: 0,
									})),
								);
								if (entity === "UNIVERSE") {
									const investmentsWithRowId = investments.map((investment): Instrument => {
										const id = investment.uuid;
										const exposure = investmentData?.investmentExposureMap.get(id);
										const investmentSummary = investmentData?.investmentSummaryMap.get(id);
										return {
											id,
											tickerName: id,
											alias: "Portfolio",
											name: investment.name,
											proxyOverwriteType: "PORTFOLIO_MIXED",
											weight: undefined,
											instrumentType: "INVESTMENT",
											investmentUuid: id,
											deleted: false,
											nofInstruments: investmentSummary?.nofInstruments,
											macroAssetClassExposure: exposure?.investmentComposition,
											macroAssetClassExposureEnhanced: exposure?.enhancementComposition,
											lastActionNote: investmentSummary?.lastActionNote,
											action: investmentSummary?.action,
										};
									});
									instrumentBuilder.bulkAddInstruments(investmentsWithRowId);
									return;
								}

								const investmentsWithRowId = investments.map((investment): Instrument => {
									const id = investment.uuid;
									const exposure = investmentData?.investmentExposureMap.get(id);
									const investmentSummary = investmentData?.investmentSummaryMap.get(id);
									const someInstrumentAreRemoved = investmentData?.verifiedInvestmentsMap.get(id) ?? [];
									return {
										id,
										tickerName: id,
										alias: "Portfolio",
										name: investment.name,
										proxyOverwriteType: "PORTFOLIO_MIXED",
										weight: 0,
										instrumentType: "INVESTMENT",
										investmentUuid: id,
										deleted: false,

										nofInstruments: investmentSummary?.nofInstruments,
										macroAssetClassExposure: exposure?.investmentComposition,
										macroAssetClassExposureEnhanced: exposure?.enhancementComposition,
										lastActionNote: investmentSummary?.lastActionNote,
										action: investmentSummary?.action,

										nOfInstrumentExcluded: someInstrumentAreRemoved.length,
										someInstrumentsAreExcluded: someInstrumentAreRemoved.length > 0,
									};
								});
								instrumentBuilder.bulkAddInstruments(investmentsWithRowId);
							},
						}),
					"data-qualifier": "CompositionEditor/HeaderAction/DropdownMenu(AddPortfolio)",
				}),
				uploadInstruments: () => ({
					label: "Upload composition",
					onClick: () =>
						spawnInstrumentUploadDialogProps({
							entity,
							compositionMode: quantityBuilder ? "quantity" : "weight",
							composition: covertInstrumentEditorToReviewTicker(
								Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()).filter(
									(row) => !row.isCash,
								),
							),
							async onSubmitAsync(newComposition, policy) {
								const userInstrumentsMap = await reviewTickerToInstrumentsMap(newComposition);
								const instrumentsComposition = createInstrumentList({
									userInstrumentsMap,
									composition: newComposition,
								});
								if (quantityBuilder) {
									quantityBuilder.uploadQuantities(instrumentsComposition, policy);
									return;
								}
								instrumentBuilder.reset((innerBuilder) => ({
									composition: OrderedMap(instrumentsComposition.map((x) => [x.id, x])),
									cash: innerBuilder.cash,
									computationPrecision: innerBuilder.computationPrecision,
									deleted: OrderedSet(),
								}));
							},
						}),
					"data-qualifier": "CompositionEditor/HeaderAction/DropdownMenu(Upload)",
				}),
				copyComposition: () => ({
					label: "Copy composition",
					onClick: () => {
						if (entity === "INVESTMENT_ENHANCEMENT" || entity === "UNIVERSE") {
							platformToast({
								children: "Entity not valid choose one between reference or portfolio",
								severity: "error",
								icon: "Portfolio",
							});
							return;
						}

						spawnCopyCompositionDialog({
							entity:
								entity === "INVESTMENT_DRAFT"
									? "INVESTMENT"
									: entity === "BENCHMARK"
										? "CUSTOM_BENCHMARK"
										: entity === "TARGET_INVESTMENT"
											? "REFERENCE_INVESTMENT"
											: entity, //fixme: BE unconsistency for naming entities
							uuid,
							initialSidePanelFilters: {
								search: "",
								filters: [
									{
										key: "TYPE",
										relation: "EQUAL",
										type: "FINITE_SET_TYPE",
										value: ["REFERENCE_INVESTMENT", "INVESTMENT"] satisfies CompositionTemplateEntityType[],
									},
								],
							},
							onSubmitAsync: (data) =>
								runWithErrorReporting(
									async () => {
										const { instruments, cashTicker } = await instrumentsRepository.copyComposition.fetchQuery(
											data.uuid,
											data.type,
										);

										instrumentBuilder.reset(({ cash }) => ({
											composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
											cash: cashTicker ? cashTickerToInstrument(cashTicker) : cash,
										}));
									},
									{
										area: "portfolio",
										attemptedOperation: {
											message: "attempt to load entity template",
											payload: JSON.stringify(data),
										},
									},
								),
						});
					},
					"data-qualifier": "CompositionEditor/HeaderAction/DropdownMenu(CopyComposition)",
				}),
			}) satisfies Record<
				HeaderActions,
				ActionWithOptionalGroup<string> | ((...params: any[]) => ActionWithOptionalGroup<string>)
			>,
		[entity, instrumentBuilder, quantityBuilder, uuid],
	);
}

export type InstrumentEditorBuilderProps = {
	getTotal(key: Extract<keyof Instrument, "score" | "weight">): number;
	getWeight(ticker: string): number | null;
	getDeleted(): Set<string>;
	getComposition(opts?: { removeDeleted?: boolean }): Map<string, Instrument>;
	latestComposition(): Map<string, Instrument>;

	latestDeleted(): Set<string>;
	delete(mode: "soft", keys: string | string[] | Iterable<string>, toggle: boolean): InstrumentEditor;
	delete(mode: "hard", keys: string | string[] | Iterable<string>): InstrumentEditor;
	setWeight(ticker: string, newWeight?: number): InstrumentEditor;
	setInstrument(key: string, entry: Instrument): InstrumentEditor;
	addInstrument(key: string, entry: Instrument): InstrumentEditor;
	bulkAddInstruments(entry: Instrument[]): InstrumentEditor;
	bulkEditInstruments(keys: Set<string>, provider: (entry: Instrument) => Instrument): InstrumentEditor;
	getInstrument(key: string): Instrument | undefined;

	getCash(): Instrument | undefined;

	normalise(distributeTo: Set<string>): InstrumentEditor;
	excessToCash(): InstrumentEditor;
	canExcessToCash(): boolean;
	isInstrumentDeleted(id: string): boolean;
	latestComparedInstruments: () => Map<string, Instrument>;
	setInstrumentToCompare(investments: Instrument[]): InstrumentEditor;
	deleteComparedInstruments(keys: string | Iterable<string>): InstrumentEditor;
	getComparedInstrument(key: string): Instrument | undefined;
	clearComparedInstruments(): InstrumentEditor;
	isDirty: boolean;

	reset(
		resetDataOrFn: MaybeFn<
			{
				composition: Map<string, Instrument>;
				deleted?: Set<string>;
				cash?: Instrument;
				computationPrecision?: number;
				comparedInstruments?: Map<string, Instrument>;
			},
			[
				{
					composition: Map<string, Instrument>;
					deleted?: Set<string>;
					cash?: Instrument;
					computationPrecision?: number;
					comparedInstruments?: Map<string, Instrument>;
				},
			]
		>,
		opt?: { disableToggleDirty?: boolean },
	): void;
};

type InstrumentBuilderInit = {
	composition: Map<string, Instrument>;
	deleted?: Set<string>;
	cash?: Instrument;
	computationPrecision?: number;
	comparedInstruments?: Map<string, Instrument>;
};
/**
 * A hook that creates a {@link InstrumentEditor composition builder} and wraps every direct operation that would
 * normally return a new instance in a React "setState".
 *
 * Example:
 * ```tsx
 * const compositionBuilder = useCompositionBuilder(() => ({
 * 	composition: Map<string, number>([["demo", 20]]),
 * }));
 *
 * return (
 * 	<Button onClick={() => compositionBuilder.setWeight("demo", 30)}>
 * 		Current weight: {compositionBuilder.getWeight("demo")?.toNumber() ?? '-'}
 * 	</Button>
 * );
 * ```
 *
 * @param initializer The initial value or a function that produces it.
 */
export function useInstrumentEditorBuilder(initializer: InstrumentBuilderInit): InstrumentEditorBuilderProps {
	const [builder, setBuilder] = useState(() => {
		const { composition, computationPrecision, deleted, cash, comparedInstruments } = initializer;
		return new InstrumentEditor(composition, deleted, cash, computationPrecision, comparedInstruments);
	});

	const [isDirty, setIsDirty] = useState(false);

	const latestCompositionBuilderRef = useRef(builder);
	const updateBuilder = useCallback((newBuilder: InstrumentEditor, opt?: { disableToggleDirty?: boolean }) => {
		if (!opt?.disableToggleDirty) {
			setIsDirty(true);
		}
		setBuilder(newBuilder);
		latestCompositionBuilderRef.current = newBuilder;
		return newBuilder;
	}, []);

	return useMemo(() => {
		noop(builder); // track to trigger re-renders
		return {
			setWeight: (...args) => updateBuilder(latestCompositionBuilderRef.current.setWeight(...args)),
			getWeight: (...args) => latestCompositionBuilderRef.current.getWeight(...args),
			excessToCash: (...args) => updateBuilder(latestCompositionBuilderRef.current.excessToCash(...args)),
			canExcessToCash: () => latestCompositionBuilderRef.current.canExcessToCash(),
			normalise: (...args) => updateBuilder(latestCompositionBuilderRef.current.normalise(...args)),
			getTotal: (...args) => latestCompositionBuilderRef.current.getTotal(...args),
			getComposition: (...args) => latestCompositionBuilderRef.current.getComposition(...args),
			setInstrument: (...args) => updateBuilder(latestCompositionBuilderRef.current.setInstrument(...args)),
			addInstrument: (...args) => updateBuilder(latestCompositionBuilderRef.current.addInstrument(...args)),
			bulkAddInstruments: (...args) => updateBuilder(latestCompositionBuilderRef.current.bulkAddInstruments(...args)),
			bulkEditInstruments: (...args) => updateBuilder(latestCompositionBuilderRef.current.bulkEditInstruments(...args)),
			getInstrument: (...args) => latestCompositionBuilderRef.current.getInstrument(...args),
			latestComposition: () => latestCompositionBuilderRef.current.composition,
			latestDeleted: () => latestCompositionBuilderRef.current.deleted,
			latestComparedInstruments: () => latestCompositionBuilderRef.current.comparedInstruments,
			getCash: () => latestCompositionBuilderRef.current.getCash(),
			getDeleted: (...args) => latestCompositionBuilderRef.current.getDeleted(...args),
			isInstrumentDeleted: (...args) => latestCompositionBuilderRef.current.isInstrumentDeleted(...args),
			setInstrumentToCompare: (...args) =>
				updateBuilder(latestCompositionBuilderRef.current.setInstrumentToCompare(...args)),
			deleteComparedInstruments: (...args) =>
				updateBuilder(latestCompositionBuilderRef.current.deleteComparedInstruments(...args)),
			getComparedInstrument: (...args) => latestCompositionBuilderRef.current.getComparedInstrument(...args),
			clearComparedInstruments: (...args) =>
				updateBuilder(latestCompositionBuilderRef.current.clearComparedInstruments(...args)),
			delete: (
				...args:
					| [mode: "soft", keys: string | Iterable<string>, toggle: boolean]
					| [mode: "hard", keys: string | Iterable<string>]
			) => {
				if (args[0] === "hard") {
					return updateBuilder(latestCompositionBuilderRef.current.delete(...args));
				}
				return updateBuilder(latestCompositionBuilderRef.current.delete(...args));
			},
			reset(resetDataOrFn: MaybeFn<InstrumentBuilderInit, [InstrumentBuilderInit]>, opt) {
				if (typeof resetDataOrFn === "function") {
					const { composition, deleted, cash, computationPrecision, comparedInstruments } = resetDataOrFn(
						latestCompositionBuilderRef.current,
					);
					updateBuilder(
						new InstrumentEditor(composition, deleted, cash, computationPrecision, comparedInstruments),
						opt,
					);
					return;
				}

				const { composition, deleted, cash, computationPrecision, comparedInstruments } = resetDataOrFn;
				updateBuilder(new InstrumentEditor(composition, deleted, cash, computationPrecision, comparedInstruments), opt);
			},
			isDirty,
		};
	}, [builder, isDirty, updateBuilder]);
}

/**
 * A class that represents a collection of instruments that constitute a composition and exposes utility methods to interact with it.
 * A composition is the combination of tickers and their respective weights. When normalised, all the weights should
 * sum up to 100 (%).
 */
export class InstrumentEditor
	implements
		Omit<
			InstrumentEditorBuilderProps,
			| "reset"
			| "latestComposition"
			| "latestTags"
			| "latestDeleted"
			| "latestComparedInstruments"
			| "canExcessToCash"
			| "isDirty"
		>
{
	composition: Map<string, Instrument>;
	deleted: Set<string>;
	cash?: Instrument;
	computationPrecision: number;
	comparedInstruments: Map<string, Instrument>;
	constructor(
		composition: Map<string, Instrument>,
		deleted?: Set<string>,
		cash?: Instrument,
		computationPrecision?: number,
		comparedInstruments?: Map<string, Instrument>,
	) {
		this.composition = composition;
		this.deleted = deleted ?? OrderedSet();
		this.cash = cash;
		// Must stay finer than MAX_PERCENT_DECIMAL_PLACES, so a normalised total rounds to 100 at every client precision.
		this.computationPrecision = computationPrecision ?? 14;
		this.comparedInstruments = comparedInstruments ?? OrderedMap();
	}

	bulkEditInstruments(keys: Set<string>, provider: (entry: Instrument) => Instrument): InstrumentEditor {
		const newComposition = this.composition.flatMap<string, Instrument>((instrument) => {
			if (keys.has(instrument.id)) {
				return [[instrument.id, provider(instrument)]];
			}
			return [[instrument.id, instrument]];
		});

		return new InstrumentEditor(
			newComposition,
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments.clear(),
		);
	}

	clearComparedInstruments(): InstrumentEditor {
		return new InstrumentEditor(
			this.composition,
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments.clear(),
		);
	}

	getComparedInstrument(key: string): Instrument | undefined {
		return this.comparedInstruments.get(key);
	}

	deleteComparedInstruments(keys: string | Iterable<string>): InstrumentEditor {
		return new InstrumentEditor(
			this.composition,
			this.deleted,
			this.cash,
			this.computationPrecision,
			typeof keys === "string" ? this.comparedInstruments.delete(keys) : this.comparedInstruments.deleteAll(keys),
		);
	}

	setInstrumentToCompare(investments: Instrument[]): InstrumentEditor {
		const investmentsToCompare = OrderedMap(
			investments.flatMap((investment) => {
				const instrument = this.comparedInstruments?.has(investment.id);
				return instrument ? [] : [[investment.id, investment]];
			}),
		);

		return new InstrumentEditor(
			this.composition,
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments?.merge(investmentsToCompare),
		);
	}

	getCash(): Instrument | undefined {
		return this.cash;
	}

	setInstrument(key: string, entry: Instrument): InstrumentEditor {
		return new InstrumentEditor(
			this.composition.set(key, entry),
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments,
		);
	}

	getInstrument(key: string): Instrument | undefined {
		return this.composition.get(key);
	}

	addInstrument(key: string, entry: Instrument): InstrumentEditor {
		const compositionArray = this.composition.toArray();
		compositionArray.unshift([key, entry]);
		return new InstrumentEditor(
			OrderedMap(compositionArray),
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments,
		);
	}

	bulkAddInstruments(instruments: Instrument[]): InstrumentEditor {
		const instrumentsToAdd = instruments.flatMap<[string, Instrument]>((instrument) => {
			return this.composition?.has(instrument.id) ? [] : [[instrument.id, instrument]];
		});
		const compositionArray = this.composition.toArray();
		compositionArray.unshift(...instrumentsToAdd);
		return new InstrumentEditor(
			OrderedMap(compositionArray),
			this.deleted,
			this.cash,
			this.computationPrecision,

			this.comparedInstruments,
		);
	}

	delete(mode: "soft", keys: string | string[] | Iterable<string>, toggle: boolean): InstrumentEditor;
	delete(mode: "hard", keys: string | string[] | Iterable<string>): InstrumentEditor;
	delete(mode: "hard" | "soft", keys: string | Iterable<string>, toggle?: boolean): InstrumentEditor {
		if (mode === "hard") {
			return new InstrumentEditor(
				typeof keys === "string" ? this.composition.delete(keys) : this.composition.deleteAll(keys),
				this.deleted,
				this.cash,
				this.computationPrecision,
				this.comparedInstruments,
			);
		}
		function applySoftDeleteToInstrumentMap(
			instrumentKeys: string | Iterable<string>,
			instrumentMap: InstrumentEditor["composition"],
		) {
			if (typeof instrumentKeys === "string") {
				const instrument = instrumentMap.get(instrumentKeys)!;
				return instrumentMap.set(instrumentKeys, { ...instrument, deleted: toggle });
			}

			const instrumentsToUpdate = Array.from(instrumentKeys).map((id): [string, Instrument] => {
				const instrument = instrumentMap.get(id)!;
				return [id, { ...instrument, deleted: toggle }];
			});

			return instrumentMap.merge(instrumentsToUpdate);
		}
		const newCompositionMap = applySoftDeleteToInstrumentMap(keys, this.composition);

		return new InstrumentEditor(
			newCompositionMap,
			typeof keys === "string"
				? toggle
					? this.deleted.add(keys)
					: this.deleted.remove(keys)
				: toggle
					? this.deleted.union(keys)
					: this.deleted.subtract(keys),
			this.cash,
			this.computationPrecision,
			this.comparedInstruments,
		);
	}

	setWeight(ticker: string, newWeight?: number): InstrumentEditor {
		const entry = this.composition.get(ticker);
		if (entry) {
			return new InstrumentEditor(
				this.composition.set(ticker, {
					...entry,
					weight: newWeight,
				}),
				this.deleted,
				this.cash,
				this.computationPrecision,
				this.comparedInstruments,
			);
		}

		return new InstrumentEditor(
			this.composition,
			this.deleted,
			this.cash,
			this.computationPrecision,
			this.comparedInstruments,
		);
	}

	getWeight(ticker: string): number | null {
		const weight = this.composition.get(ticker)?.weight;
		return weight ?? null;
	}

	canExcessToCash(): boolean {
		if (this.cash === undefined) {
			return false;
		}
		if (BigNumber(this.getTotal("weight")).gt(100)) {
			return false;
		}

		return true;
	}

	excessToCash(): InstrumentEditor {
		if (this.cash === undefined || !this.canExcessToCash()) {
			console.warn("unable to perform excess to cash");
			return this;
		}

		const totalExcludingCash = this.composition
			.delete(this.cash.id)
			.removeAll(this.deleted)
			.reduce((sum, cur) => sum.plus(cur.weight ?? 0), BigNumber(0));

		return this.setInstrument(this.cash.id, {
			...this.cash,
			weight: BigNumber(100)
				.minus(totalExcludingCash)
				.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR)
				.toNumber(),
		});
	}

	normalise(distributeTo: Iterable<string>): InstrumentEditor {
		const nonDeletedTickerIds = Array.from(this.composition.removeAll(this.deleted).keys());

		const totalOverOrUndershot = BigNumber(this.getTotal("weight"))
			.minus(100)
			.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR);

		if (totalOverOrUndershot.eq(0)) {
			// Nothing to normalise.
			return this;
		}

		const editableTickerIds = OrderedSet(distributeTo).subtract(this.deleted);
		const totalWeightOfSelected = editableTickerIds.reduce(
			(sum, cur) => sum.plus(this.composition.get(cur)?.weight ?? 0),
			BigNumber(0),
		);
		if (totalWeightOfSelected.lt(totalOverOrUndershot)) {
			// Normalisation is impossible to achieve but we can approach it by setting all the weights to 0.
			return new InstrumentEditor(
				this.composition.withMutations((editableCompositionEntry) => {
					for (const tickerId of editableTickerIds) {
						const entry = editableCompositionEntry.get(tickerId);
						if (entry) {
							editableCompositionEntry.set(tickerId, { ...entry, weight: 0 });
						}
					}
				}),
				this.deleted,
				this.cash,
				this.computationPrecision,
				this.comparedInstruments,
			);
		}

		return new InstrumentEditor(
			this.composition.withMutations((editableCompositionEntry) => {
				const sumOfSelected = editableTickerIds.reduce(
					(sum, cur) => sum.plus(this.composition.get(cur)?.weight ?? 0),
					BigNumber(0),
				);
				if (sumOfSelected.gt(0)) {
					for (const tickerId of editableTickerIds) {
						const entry = editableCompositionEntry.get(tickerId);
						if (entry) {
							const scaledNudge = totalOverOrUndershot
								.negated()
								.times(entry.weight ?? 0)
								.div(sumOfSelected);
							editableCompositionEntry.set(tickerId, {
								...entry,
								weight: BigNumber.min(
									100,
									BigNumber.max(
										0,
										BigNumber(entry.weight ?? 0)
											.plus(scaledNudge)
											.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR),
									),
								).toNumber(),
							});
						}
					}
				}

				const MAX_ATTEMPTS = 10;

				outer: for (let i = 0; i < MAX_ATTEMPTS; i++) {
					const remainingOverOrUndershot = nonDeletedTickerIds
						.reduce((sum, cur) => sum.plus(editableCompositionEntry.get(cur)?.weight ?? 0), BigNumber(0))
						.minus(100)
						.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR);
					if (remainingOverOrUndershot.isZero()) {
						break;
					}

					const nudge = remainingOverOrUndershot.isNegative()
						? BigNumber.max(this.getStep(), remainingOverOrUndershot.negated().div(editableTickerIds.size))
						: BigNumber.min(-this.getStep(), remainingOverOrUndershot.negated().div(editableTickerIds.size));

					for (const tickerId of editableTickerIds) {
						const entry = editableCompositionEntry.get(tickerId);
						if (entry) {
							editableCompositionEntry.set(tickerId, {
								...entry,
								weight: BigNumber.min(
									100,
									BigNumber.max(
										0,
										BigNumber(entry.weight ?? 0)
											.plus(nudge)
											.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR),
									),
								).toNumber(),
							});

							const remaining = nonDeletedTickerIds
								.reduce((sum, cur) => sum.plus(editableCompositionEntry.get(cur)?.weight ?? 0), BigNumber(0))
								.minus(100)
								.decimalPlaces(this.computationPrecision, BigNumber.ROUND_FLOOR);
							if (remaining.isZero()) {
								break outer;
							}
						}
					}
				}
			}),
			this.deleted,
			this.cash,
			this.computationPrecision,

			this.comparedInstruments,
		);
	}

	getTotal(key: Extract<keyof Instrument, "score" | "weight">): number {
		const compositionWithoutDeleted = this.composition.removeAll(this.deleted);
		return compositionWithoutDeleted.reduce((sum, cur) => sum.plus(cur[key] ?? 0), BigNumber(0)).toNumber();
	}

	getTotalWeightWithoutCash(): number {
		if (this.cash === undefined) {
			return this.getTotal("weight");
		}
		const compositionWithoutDeleted = this.composition.removeAll(this.deleted).delete(this.cash.id);
		return compositionWithoutDeleted.reduce((sum, cur) => sum.plus(cur.weight ?? 0), BigNumber(0)).toNumber();
	}

	getDeleted(): Set<string> {
		return this.deleted;
	}

	getStep(): BigNumber {
		return BigNumber(10).pow(-this.computationPrecision);
	}

	isInstrumentDeleted(id: string): boolean {
		return this.deleted.has(id);
	}

	getComposition(opts?: { removeDeleted?: boolean }): Map<string, Instrument> {
		if (opts?.removeDeleted) {
			return this.composition.removeAll(this.deleted);
		}
		return this.composition;
	}
}

export function getTotalAllocationWeight(allocation: Iterable<number | BigNumber>): BigNumber {
	let sum = BigNumber(0);
	for (const weight of allocation) {
		sum = sum.plus(weight);
	}
	return sum;
}

export function normalise(params: {
	distributeTo: Iterable<string>;
	allocation: OrderedMap<string, BigNumber>;
	decimalPlaces: number;
	target?: number;
}): OrderedMap<string, BigNumber> {
	const target = params.target ?? 100;
	const allocationIds = Array.from(params.allocation.keys());

	const totalOverOrUndershot = BigNumber(getTotalAllocationWeight(params.allocation.values()))
		.minus(target)
		.decimalPlaces(params.decimalPlaces, BigNumber.ROUND_FLOOR);

	if (totalOverOrUndershot.eq(0)) {
		// Nothing to normalise.
		return params.allocation;
	}

	const editableTickerIds = OrderedSet(params.distributeTo);
	const totalWeightOfSelected = editableTickerIds.reduce(
		(sum, cur) => sum.plus(params.allocation.get(cur) ?? 0),
		BigNumber(0),
	);
	if (totalWeightOfSelected.lt(totalOverOrUndershot)) {
		// Normalisation is impossible to achieve but we can approach it by setting all the weights to 0.
		const newAllocation = params.allocation.withMutations((editableCompositionEntry) => {
			for (const tickerId of editableTickerIds) {
				const entry = editableCompositionEntry.get(tickerId);
				if (entry) {
					editableCompositionEntry.set(tickerId, BigNumber(0));
				}
			}
		});

		return newAllocation;
	}

	const newAllocation = params.allocation.withMutations((editableCompositionEntry) => {
		const sumOfSelected = editableTickerIds.reduce(
			(sum, cur) => sum.plus(params.allocation.get(cur) ?? 0),
			BigNumber(0),
		);
		if (sumOfSelected.gt(0)) {
			for (const tickerId of editableTickerIds) {
				const entry = editableCompositionEntry.get(tickerId);
				if (entry) {
					const scaledNudge = totalOverOrUndershot
						.negated()
						.times(entry ?? 0)
						.div(sumOfSelected);
					editableCompositionEntry.set(
						tickerId,
						BigNumber.min(
							target,
							BigNumber.max(
								0,
								BigNumber(entry ?? 0)
									.plus(scaledNudge)
									.decimalPlaces(params.decimalPlaces, BigNumber.ROUND_FLOOR),
							),
						),
					);
				}
			}
		}

		const MAX_ATTEMPTS = 10;

		outer: for (let i = 0; i < MAX_ATTEMPTS; i++) {
			const remainingOverOrUndershot = allocationIds
				.reduce((sum, cur) => sum.plus(editableCompositionEntry.get(cur) ?? 0), BigNumber(0))
				.minus(target)
				.decimalPlaces(params.decimalPlaces, BigNumber.ROUND_FLOOR);
			if (remainingOverOrUndershot.isZero()) {
				break;
			}

			const nudge = remainingOverOrUndershot.isNegative()
				? BigNumber.max(getStep(params.decimalPlaces), remainingOverOrUndershot.negated().div(editableTickerIds.size))
				: BigNumber.min(-getStep(params.decimalPlaces), remainingOverOrUndershot.negated().div(editableTickerIds.size));

			for (const tickerId of editableTickerIds) {
				const entry = editableCompositionEntry.get(tickerId);
				if (entry) {
					editableCompositionEntry.set(
						tickerId,
						BigNumber.min(
							target,
							BigNumber.max(
								0,
								BigNumber(entry ?? 0)
									.plus(nudge)
									.decimalPlaces(params.decimalPlaces, BigNumber.ROUND_FLOOR),
							),
						),
					);

					const remaining = allocationIds
						.reduce((sum, cur) => sum.plus(editableCompositionEntry.get(cur) ?? 0), BigNumber(0))
						.minus(target)
						.decimalPlaces(params.decimalPlaces, BigNumber.ROUND_FLOOR);
					if (remaining.isZero()) {
						break outer;
					}
				}
			}
		}
	});

	return newAllocation;
}

/**
 * Scales `proportions` (each 0–100, summing to 100) to `target` using the same
 * proportional-floor + iterative-correction algorithm as normalise().
 * Guarantees sum(result) === floor(target, decimalPlaces) exactly.
 */
export function scaleAndNormalize(proportions: number[], target: number, decimalPlaces: number): number[] {
	if (proportions.length === 0) return [];

	const keys = proportions.map((_, idx) => String(idx));
	const allocation = OrderedMap(
		keys.map((key, idx) => [
			key,
			BigNumber(target).times(proportions[idx]).div(100).decimalPlaces(decimalPlaces, BigNumber.ROUND_FLOOR),
		]),
	);
	const normalised = normalise({ distributeTo: keys, allocation, decimalPlaces, target });
	return keys.map((key) => normalised.get(key)?.toNumber() ?? 0);
}

function getStep(decimalPlaces: number): BigNumber {
	return BigNumber(10).pow(-decimalPlaces);
}
